import { NgModule, APP_INITIALIZER } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { LocationStrategy, HashLocationStrategy, DatePipe } from '@angular/common';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ProductModule } from './products/product.module';
import { WelcomeComponent } from './home/welcome.component';
import { CreateAccountComponent } from './account/create-account.component';
import { CreateAccountService } from './account/create-account.service';
import { LoginService } from './home/login.service';
import { LoginComponent } from './login/login.component';
import { CartComponent } from './cart/cart.component';
import { CheckoutComponent } from './checkout/checkout.component';
import { CheckoutService } from './checkout/checkout.service';
import { ShippingInfoComponent } from './checkout/shippinginfo.component';
import { SeamlessAuthComponent } from './seamlessauth/seamlessauth.component';
import { ValidateAddressComponent } from './account/validate-address.component';
import { PasswordResetComponent } from './login/passwordreset.component';
import { ValidateResetComponent } from './login/validatekey.component';
import { AuthGuard } from './home/auth-guard.service';
import { CompletedComponent } from './completed/completed.component';
import { ProductListComponent } from './products/product-list.component';
import { ProductDetailComponent } from './products/product-detail.component';
import { NgxSpinnerModule } from 'ngx-spinner';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader, TRANSLATE_HTTP_LOADER_CONFIG  } from '@ngx-translate/http-loader';
import { ToastrModule, ToastrService } from 'ngx-toastr';
import { OnlineOrderService } from './onlineorder/onlineorder.service';
import { OrderService } from './onlineorder/order.service';
import { AdminService } from './admin/admin.service';
import { NuveiPaymentsComponent } from './nuvei/nuveipayments.component';
import { LogRocketService } from './logrocket/logrocket.service';
import { AlreadyLoggedInGuard } from './home/loggedin-auth-guard.service';
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from 'ngx-mask';
import { AppConfigService } from './admin/app-config.service';

export function initializeApp(configService: AppConfigService) {
  return () => configService.load();
}

@NgModule({
  declarations: [
    AppComponent,
    WelcomeComponent,
    CreateAccountComponent,
    NuveiPaymentsComponent,
    LoginComponent,
    CartComponent,
    SeamlessAuthComponent,
    PasswordResetComponent,
    ValidateResetComponent,
    CheckoutComponent,
    ShippingInfoComponent,
    CompletedComponent,
    ValidateAddressComponent
  ],
  imports: [
    AppRoutingModule,
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    ReactiveFormsModule,
    NgbModule,
    NgxSpinnerModule.forRoot({ type: 'ball-scale-multiple' }),
    NgxMaskDirective,
    NgxMaskPipe,
    TranslateModule.forRoot({
      defaultLanguage: 'en',
      loader: {
        provide: TranslateLoader,
        useClass: TranslateHttpLoader
      }
    }),
    ToastrModule.forRoot({
      positionClass: 'toast-bottom-right',
      newestOnTop: true,
      preventDuplicates: true,
      progressAnimation: 'increasing',
      toastClass: 'ngx-toastr',
      tapToDismiss: true
    }),
    RouterModule.forRoot([
      { path: 'home/from/:id', component: WelcomeComponent },
      { path: 'shop/from/:id', component: ProductListComponent },
      { path: 'detail/from/:id:prodId', component: ProductDetailComponent },
      { path: 'seamless/from/:id', component: SeamlessAuthComponent },
      { path: 'createAccount/from/:id', component: CreateAccountComponent, canActivate: [AlreadyLoggedInGuard] },
      { path: 'checkout/from/:id', component: CheckoutComponent, canActivate: [AuthGuard] },
      { path: 'completed/from/:id', component: CompletedComponent, canActivate: [AuthGuard] },
      { path: 'reset/from/:id', component: PasswordResetComponent },
      { path: 'validate/for/:id:key', component: ValidateResetComponent },
      { path: 'login', component: LoginComponent, canActivate: [AlreadyLoggedInGuard] },
      { path: 'cart/from/:id', component: CartComponent },
      { path: '', redirectTo: 'shop/from/:id', pathMatch: 'full' },
      { path: '**', redirectTo: 'shop/from/:id', pathMatch: 'full' }
    ]),
    ProductModule
  ],
  providers: [
    provideHttpClient(withInterceptorsFromDi()),
    provideNgxMask({ validation: false }),
    {
      provide: TRANSLATE_HTTP_LOADER_CONFIG,
      useValue: { prefix: './assets/i18n/', suffix: '.json' }
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [AppConfigService],
      multi: true,
    },
    AppConfigService,
    CreateAccountService,
    LoginService,
    CheckoutService,
    OnlineOrderService,
    LogRocketService,
    OrderService,
    { provide: LocationStrategy, useClass: HashLocationStrategy },
    AuthGuard,
    DatePipe,
    ToastrService,
    AdminService
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }